import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/LangContext";

// NB bounding box (approx real): lat 44.5-48.1 N, lon -69.1 to -63.5 W
const LAT_MAX = 48.2;
const LAT_MIN = 44.4;
const LON_MIN = -69.15;
const LON_MAX = -63.4;

const W = 800;
const H = 900;

function project(coords) {
  const parts = coords.split(",").map((s) => s.trim());
  const latN = parseFloat(parts[0]);
  const lonN = parseFloat(parts[1]);
  const lat = parts[0].includes("S") ? -latN : latN;
  const lon = parts[1].includes("W") ? -lonN : lonN;
  const x = ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * W;
  const y = ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * H;
  return { x, y };
}

// Approximate NB outline polygon (lat, lon pairs)
const NB_OUTLINE = [
  [48.05, -69.05],
  [47.95, -68.15],
  [48.05, -67.15],
  [48.05, -66.35],
  [47.95, -66.15],
  [47.85, -65.65],
  [47.80, -64.85],
  [47.55, -64.60],
  [47.10, -64.60],
  [46.85, -64.85],
  [46.55, -64.65],
  [46.20, -64.20],
  [46.00, -63.85],
  [45.85, -64.30],
  [45.75, -64.55],
  [45.65, -64.80],
  [45.55, -65.20],
  [45.20, -65.50],
  [45.05, -65.80],
  [45.05, -66.10],
  [45.20, -66.10],
  [45.20, -66.55],
  [45.10, -66.85],
  [44.90, -66.85],
  [44.55, -67.05],
  [45.15, -67.35],
  [45.55, -67.45],
  [45.95, -67.75],
  [46.30, -67.80],
  [46.50, -67.85],
  [46.90, -67.75],
  [47.10, -68.05],
  [47.25, -68.30],
  [47.35, -68.50],
  [47.45, -68.70],
  [47.65, -68.95],
  [47.90, -69.10],
];

const outlinePath =
  NB_OUTLINE.map(([lat, lon], i) => {
    const x = ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * W;
    const y = ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * H;
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ") + " Z";

export default function InteractiveMap() {
  const { cities: CITIES, lang } = useLang();
  const [hovered, setHovered] = useState(null);
  const [regionFilter, setRegionFilter] = useState(null);

  const REGIONS = Array.from(new Set(CITIES.map((c) => c.region)));

  const visible = regionFilter
    ? CITIES.filter((c) => c.region === regionFilter)
    : CITIES;

  return (
    <section
      data-testid="nb-map"
      className="border-t border-border bg-background"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-8">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
              [ {lang === "fr" ? "Carte provinciale" : "Provincial Map"} · {CITIES.length} {lang === "fr" ? "communautés" : "Communities"} ]
            </div>
            <h2 className="font-serif font-light text-4xl md:text-7xl leading-[0.95] tracking-tighter">
              {lang === "fr" ? (
                <>Touchez une <span className="whitespace-nowrap">communauté.</span> <br /><i className="text-primary">Déployez là-bas.</i></>
              ) : (
                <>Tap a Community. <br /><i className="text-primary">Deploy There.</i></>
              )}
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="text-foreground/70 leading-relaxed">
              {lang === "fr"
                ? `Chacune des ${CITIES.length} communautés constituées que nous servons est tracée par ses coordonnées géographiques réelles. Filtrez par région ou touchez un marqueur pour sauter à la page de couverture dédiée de cette ville.`
                : `Every one of the ${CITIES.length} incorporated communities we serve is plotted by real geographic coordinates. Filter by region or tap a marker to jump to that city's dedicated coverage page.`}
            </p>
          </div>
        </div>

        {/* Region filter */}
        <div className="mb-8 flex flex-wrap gap-2">
          <button
            onClick={() => setRegionFilter(null)}
            data-testid="map-region-all"
            className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors ${
              !regionFilter
                ? "bg-primary border-primary text-white"
                : "border-border text-muted-foreground hover:border-primary hover:text-primary"
            }`}
          >
            {lang === "fr" ? "Toutes" : "All"} · {CITIES.length}
          </button>
          {REGIONS.map((r) => {
            const count = CITIES.filter((c) => c.region === r).length;
            return (
              <button
                key={r}
                onClick={() => setRegionFilter(regionFilter === r ? null : r)}
                data-testid={`map-region-${r.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors ${
                  regionFilter === r
                    ? "bg-primary border-primary text-white"
                    : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                }`}
              >
                {r} · {count}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="relative border border-border bg-[hsl(0_0%_6%)] aspect-[8/9] overflow-hidden">
              {/* subtle grid backdrop */}
              <div className="absolute inset-0 arch-grid opacity-[0.06]" />

              <svg
                viewBox={`0 0 ${W} ${H}`}
                className="absolute inset-0 w-full h-full"
                style={{ overflow: "visible" }}
              >
                {/* Compass rose */}
                <g transform="translate(60, 60)" opacity="0.55">
                  <circle r="26" fill="none" stroke="hsl(0 0% 25%)" strokeWidth="1" />
                  <line x1="0" y1="-30" x2="0" y2="30" stroke="hsl(0 0% 25%)" strokeWidth="1" />
                  <line x1="-30" y1="0" x2="30" y2="0" stroke="hsl(0 0% 25%)" strokeWidth="1" />
                  <text x="0" y="-36" textAnchor="middle" fill="hsl(0 0% 55%)" fontSize="12" fontFamily="JetBrains Mono">N</text>
                </g>

                {/* Bay of Fundy label */}
                <text x="220" y="820" fill="hsl(0 0% 35%)" fontSize="14" fontFamily="Cormorant Garamond" fontStyle="italic">
                  Bay of Fundy
                </text>
                <text x="670" y="380" fill="hsl(0 0% 35%)" fontSize="14" fontFamily="Cormorant Garamond" fontStyle="italic">
                  Northumberland
                </text>
                <text x="130" y="130" fill="hsl(0 0% 35%)" fontSize="14" fontFamily="Cormorant Garamond" fontStyle="italic">
                  Restigouche
                </text>

                {/* NB outline */}
                <motion.path
                  d={outlinePath}
                  fill="hsl(0 0% 10%)"
                  stroke="hsl(0 0% 30%)"
                  strokeWidth="1.5"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, ease: [0.76, 0, 0.24, 1] }}
                />

                {/* City dots */}
                {CITIES.map((c) => {
                  const { x, y } = project(c.coords);
                  const isVisible =
                    !regionFilter || c.region === regionFilter;
                  const isHover = hovered === c.slug;
                  return (
                    <g
                      key={c.slug}
                      transform={`translate(${x}, ${y})`}
                      className="cursor-pointer"
                      onMouseEnter={() => setHovered(c.slug)}
                      onMouseLeave={() => setHovered(null)}
                      opacity={isVisible ? 1 : 0.15}
                      style={{ transition: "opacity 300ms" }}
                    >
                      <Link to={`/cities/${c.slug}`} data-testid={`map-dot-${c.slug}`}>
                        {/* outer ring pulse when highlighted */}
                        {isHover && (
                          <circle
                            r="14"
                            fill="none"
                            stroke="hsl(15 100% 50%)"
                            strokeWidth="1"
                            opacity="0.6"
                          />
                        )}
                        <circle
                          r={isHover ? 6 : 3.5}
                          fill={isHover ? "hsl(15 100% 50%)" : "hsl(0 0% 85%)"}
                          stroke="hsl(0 0% 4%)"
                          strokeWidth="1"
                          style={{ transition: "all 200ms" }}
                        />
                        <circle r="16" fill="transparent" />
                      </Link>
                    </g>
                  );
                })}

                {/* Hover label overlay */}
                <AnimatePresence>
                  {hovered && (() => {
                    const c = CITIES.find((x) => x.slug === hovered);
                    if (!c) return null;
                    const { x, y } = project(c.coords);
                    return (
                      <motion.g
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        style={{ pointerEvents: "none" }}
                      >
                        <rect
                          x={x + 12}
                          y={y - 34}
                          width={c.name.length * 12 + 32}
                          height="30"
                          fill="hsl(0 0% 4%)"
                          stroke="hsl(15 100% 50%)"
                          strokeWidth="1"
                        />
                        <text
                          x={x + 24}
                          y={y - 14}
                          fill="hsl(0 0% 96%)"
                          fontSize="14"
                          fontFamily="Cormorant Garamond"
                        >
                          {c.name}
                        </text>
                      </motion.g>
                    );
                  })()}
                </AnimatePresence>
              </svg>

              {/* Bottom-right legend */}
              <div className="absolute bottom-4 right-4 font-mono text-[9px] tracking-[0.25em] uppercase text-muted-foreground">
                Scale · 1cm ≈ 20km · Approximate
              </div>
            </div>
          </div>

          {/* Region-filtered city list */}
          <div className="lg:col-span-4">
            <div className="border border-border bg-[hsl(0_0%_6%)] p-6 md:p-8 lg:sticky lg:top-32">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
                {regionFilter
                  ? `${lang === "fr" ? "Région" : "Region"} · ${regionFilter}`
                  : lang === "fr"
                  ? "Toutes les communautés"
                  : "All Communities"}
              </div>
              <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-1">
                {visible.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/cities/${c.slug}`}
                    onMouseEnter={() => setHovered(c.slug)}
                    onMouseLeave={() => setHovered(null)}
                    data-testid={`map-list-${c.slug}`}
                    className={`flex items-center justify-between py-2 border-b border-border group ${
                      hovered === c.slug ? "text-primary" : ""
                    }`}
                  >
                    <span className="font-serif text-lg group-hover:text-primary transition-colors">
                      {c.name}
                    </span>
                    <span className="font-mono text-[9px] tracking-widest text-muted-foreground">
                      {c.coords.split(",")[0].trim()}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
